import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from '@completion/reducers';
import { NotificationService } from '../../services/notification.service';
import {
  GetAccessibleCheckSheetAssignmentIds,
  GetAssignmentsForUserSuccess,
  GetUsersWithOpenAssignmentsSuccess,
  LoadProcurementPackages,
  LoadProcurementPackagesSuccess,
  LoadSuppliers,
  LoadSuppliersSuccess,
  LoadVendorsSuccess,
  LoadVendorUsersForPackages,
  LoadVendorUsersForPackagesSuccess,
  SaveUserCheckSheetAssignments,
  SendTransmittal,
  SendTransmittalSuccess,
  SupplierActionTypes,
  UpdateVendor,
  GetScopeForUser,
  GetUsersWithOpenAssignments,
  GetAssignmentsForUser,
  LoadPackageSow,
  LoadPackageSowSuccess,
  LoadLineTagSow,
  LoadLineTagSowSuccess,
  GetAccessibleCheckSheetAssignmentIdsSuccess,
  GetCurrentUserScopeSuccess,
  GetScopeRelatedPunchesSuccess,
  UnlinkUsersAndCheckSheetAssignments,
} from '@completion/actions';
import { getCurrentProject, getSelectedProcurementPackages } from '@completion/selectors';
import { withLatestFrom } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { SupplierService } from '../../services/supplier.service';
import { ProcurementPackage, Project } from '@completion/models';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class SupplierEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly supplierService: SupplierService,
    private readonly store: Store<State>,
    private readonly toastr: NotificationService,
  ) {}

  loadProcurementPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadProcurementPackages>(SupplierActionTypes.LoadProcurementPackages),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'getProcurementPackages',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new LoadProcurementPackagesSuccess(response as any),
      }),
    ),
  );

  loadSuppliers$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadSuppliers>(SupplierActionTypes.LoadSuppliers),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'getSuppliers',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new LoadSuppliersSuccess(response as any),
      }),
    ),
  );

  loadSupplierCheckSheetAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadPackageSow>(SupplierActionTypes.LoadPackageSow),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getSelectedProcurementPackages) as Observable<ProcurementPackage[]>,
      ),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'getSupplierCheckSheetAssignments',
        serviceArgs: ([_, currentProject, packageNos]) => [currentProject.id, packageNos],
        onSuccess: (response) => new LoadPackageSowSuccess(response as any),
      }),
    ),
  );

  loadLineTagCheckSheetAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadLineTagSow>(SupplierActionTypes.LoadLineTagSow),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'getLineTagCheckSheetAssignments',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new LoadLineTagSowSuccess(response as any),
      }),
    ),
  );

  loadVendors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupplierActionTypes.LoadVendors),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'getVendors',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new LoadVendorsSuccess(response as any),
      }),
    ),
  );

  updateVendor$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateVendor>(SupplierActionTypes.UpdateVendor),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'updateVendor',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.vendor],
        onSuccess: (response) => new LoadVendorsSuccess(response as any),
      }),
    ),
  );

  loadVendorUsersForPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadVendorUsersForPackages>(SupplierActionTypes.LoadVendorUsersForPackages),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'getVendorUsersForPackages',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.procurementPackageIds],
        onSuccess: (response) => new LoadVendorUsersForPackagesSuccess(response as any),
      }),
    ),
  );

  saveUserCheckSheetAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SaveUserCheckSheetAssignments>(SupplierActionTypes.SaveUserCheckSheetAssignments),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'saveUserCheckSheetAssignments',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.userAssignments],
        onSuccess: () => null,
      }),
    ),
  );

  getAccessibleCheckSheetAssignmentIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAccessibleCheckSheetAssignmentIds>(SupplierActionTypes.GetAccessibleCheckSheetAssignmentIds),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'getAccessibleCheckSheetAssignmentIds',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new GetAccessibleCheckSheetAssignmentIdsSuccess(response as any),
      }),
    ),
  );

  sendTransmittal$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendTransmittal>(SupplierActionTypes.SendTransmittal),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'sendTransmittal',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.userChecksheetAssignments],
        onSuccess: () => new SendTransmittalSuccess(),
      }),
    ),
  );

  getCurrentUserScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupplierActionTypes.GetCurrentUserScope),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'getCurrentUserScope',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new GetCurrentUserScopeSuccess(response as any),
      }),
    ),
  );

  getScopeRelatedPunches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupplierActionTypes.GetScopeRelatedPunches),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'getScopeRelatedPunches',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new GetScopeRelatedPunchesSuccess(response as any),
      }),
    ),
  );

  getScopeForUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetScopeForUser>(SupplierActionTypes.GetScopeForUser),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'getScopeForUser',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.userId],
        // Re-using existing success action (no dedicated GetScopeForUserSuccess defined)
        onSuccess: (response) => new GetCurrentUserScopeSuccess(response as any),
      }),
    ),
  );

  getUsersWithOpenAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetUsersWithOpenAssignments>(SupplierActionTypes.GetUsersWithOpenAssignments),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'getUsersWithOpenAssignments',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new GetUsersWithOpenAssignmentsSuccess(response as any),
      }),
    ),
  );

  getAssignmentsForUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAssignmentsForUser>(SupplierActionTypes.GetAssignmentsForUser),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'getOpenAssignmentsForUser',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.userId],
        onSuccess: (response) => new GetAssignmentsForUserSuccess(response as any),
      }),
    ),
  );

  unlinkUserChecksheetAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UnlinkUsersAndCheckSheetAssignments>(SupplierActionTypes.UnlinkUsersAndCheckSheetAssignments),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.supplierService,
        method: 'unlinkUsersAndCheckSheetAssignments',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.userChecksheetAssignments],
        onSuccess: () => null,
      }),
    ),
  );
}
